/**
 * React Query hooks for the Frontier Agents panel (Sprint 282 Stream G).
 *
 * Backend: `/api/v1/tenants/{tenant_id}/frontier/*` (view level). The cache is
 * scoped by tenant because server-side isolation is tenant-scoped but hooks
 * must not bleed data across tenant switches.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient, { type PaginatedResponse } from '@/lib/api-client';
import type { Agent } from '@/features/agents/types';
import type { Hive } from '@/features/agents/hives/types';
import { useTenant } from '@/contexts/TenantContext';
import type {
  BulkStopResult,
  FrontierErrors,
  FrontierEventFeed,
  FrontierEventFeedFilters,
  FrontierRunDetail,
  FrontierRunList,
  FrontierRoutine,
  EmergencyState,
  FrontierConsent,
  FrontierInterventionItem,
  FrontierPlatformSummary,
  FrontierSummary,
  LaunchRoutineInput,
  RoutineConfig,
  RoutineCreateInput,
  SchedulePreview,
  SchedulePreviewInput,
  ScheduleTriggerInput,
} from '../types';

const keys = {
  all: (tenantId?: string) => ['frontier', tenantId] as const,
  summary: (tenantId?: string) => [...keys.all(tenantId), 'summary'] as const,
  routines: (tenantId: string | undefined, params: Record<string, unknown>) =>
    [...keys.all(tenantId), 'routines', params] as const,
  runs: (tenantId: string | undefined, params: Record<string, unknown>) =>
    [...keys.all(tenantId), 'runs', params] as const,
  run: (tenantId: string | undefined, runId: string) =>
    [...keys.all(tenantId), 'run', runId] as const,
  events: (tenantId: string | undefined, params: FrontierEventFeedFilters) =>
    [...keys.all(tenantId), 'events', params] as const,
  errors: (tenantId: string | undefined, windowHours: number) =>
    [...keys.all(tenantId), 'errors', windowHours] as const,
};

function basePath(tenantId: string): string {
  return `/api/v1/tenants/${tenantId}/frontier`;
}

function invalidateTenant(qc: ReturnType<typeof useQueryClient>, tenantId?: string) {
  void qc.invalidateQueries({ queryKey: keys.all(tenantId) });
}

// ---------------------------------------------------------------- summary

export function useFrontierSummary() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: keys.summary(tenantId),
    queryFn: async () => {
      const { data } = await apiClient.get<FrontierSummary>(
        `${basePath(tenantId!)}/summary`,
      );
      return data;
    },
    enabled: !!tenantId,
    staleTime: 30 * 1000,
  });
}

// ---------------------------------------------------------------- routines

export function useFrontierRoutines(params: { status?: string } = {}) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: keys.routines(tenantId, params),
    queryFn: async () => {
      const { data } = await apiClient.get<FrontierRoutine[]>(
        `${basePath(tenantId!)}/routines`,
        { params },
      );
      return data;
    },
    enabled: !!tenantId,
    staleTime: 30 * 1000,
  });
}

export type RoutineLifecycleAction = 'activate' | 'pause' | 'resume' | 'archive';

export function useRoutineLifecycle() {
  const qc = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async (input: { routineId: string; action: RoutineLifecycleAction }) => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.post<FrontierRoutine>(
        `${basePath(tenantId)}/routines/${input.routineId}/${input.action}`,
        {},
      );
      return data;
    },
    onSuccess: () => invalidateTenant(qc, tenantId),
  });
}

// ---------------------------------------------------------------- runs

export function useFrontierRuns(
  params: { status?: string; routine_id?: string; limit?: number; offset?: number } = {},
) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: keys.runs(tenantId, params),
    queryFn: async () => {
      const { data } = await apiClient.get<FrontierRunList>(`${basePath(tenantId!)}/runs`, {
        params,
      });
      return data;
    },
    enabled: !!tenantId,
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
  });
}

export function useFrontierRun(runId: string | undefined) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: keys.run(tenantId, runId ?? ''),
    queryFn: async () => {
      const { data } = await apiClient.get<FrontierRunDetail>(
        `${basePath(tenantId!)}/runs/${runId}`,
      );
      return data;
    },
    enabled: !!tenantId && !!runId,
    staleTime: 10 * 1000,
    refetchInterval: 30 * 1000,
  });
}

export type RunAction = 'cancel' | 'resume';

export function useRunActions() {
  const qc = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async (input: { runId: string; action: RunAction }) => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.post<{ run_id: string; status: string }>(
        `${basePath(tenantId)}/runs/${input.runId}/${input.action}`,
        {},
      );
      return data;
    },
    onSuccess: () => invalidateTenant(qc, tenantId),
  });
}

// ---------------------------------------------------------------- errors

export function useFrontierErrors(windowHours = 168) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: keys.errors(tenantId, windowHours),
    queryFn: async () => {
      const { data } = await apiClient.get<FrontierErrors>(`${basePath(tenantId!)}/errors`, {
        params: { window_hours: windowHours },
      });
      return data;
    },
    enabled: !!tenantId,
    staleTime: 60 * 1000,
  });
}

// ---------------------------------------------------------------- events (vista Eventos)

export function useFrontierEvents(params: FrontierEventFeedFilters = {}) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: keys.events(tenantId, params),
    queryFn: async () => {
      const { data } = await apiClient.get<FrontierEventFeed>(
        `${basePath(tenantId!)}/events`,
        { params },
      );
      return data;
    },
    enabled: !!tenantId,
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
  });
}

// ---------------------------------------------------------------- periodicidad (constructor)

export function useSchedulePreview() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async (input: SchedulePreviewInput) => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.post<SchedulePreview>(
        `${basePath(tenantId)}/schedule/preview`,
        input,
      );
      return data;
    },
  });
}

// ---------------------------------------------------------------- editor (ISS-641)

export function useRoutineConfig(routineId: string | null) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [...keys.all(tenantId), 'routine-config', routineId],
    queryFn: async () => {
      const { data } = await apiClient.get<RoutineConfig>(
        `${basePath(tenantId!)}/routines/${routineId}/config`,
      );
      return data;
    },
    enabled: !!tenantId && !!routineId,
    staleTime: 0,
  });
}

export function useCreateRoutine() {
  const qc = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async (payload: RoutineCreateInput) => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.post<FrontierRoutine>(
        `${basePath(tenantId)}/routines`,
        payload,
      );
      return data;
    },
    onSuccess: () => invalidateTenant(qc, tenantId),
  });
}

export function useUpdateRoutine() {
  const qc = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async (input: { routineId: string; payload: RoutineCreateInput }) => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.patch<FrontierRoutine>(
        `${basePath(tenantId)}/routines/${input.routineId}`,
        input.payload,
      );
      return data;
    },
    onSuccess: () => invalidateTenant(qc, tenantId),
  });
}

export function useUpsertTrigger() {
  const qc = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async (input: { routineId: string; payload: ScheduleTriggerInput }) => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.post(
        `${basePath(tenantId)}/routines/${input.routineId}/triggers`,
        input.payload,
      );
      return data;
    },
    onSuccess: () => invalidateTenant(qc, tenantId),
  });
}

export function useLaunchRoutine() {
  const qc = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async (input: { routineId: string; payload: LaunchRoutineInput }) => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.post<{ run_id: string; status: string }>(
        `${basePath(tenantId)}/routines/${input.routineId}/runs`,
        input.payload,
      );
      return data;
    },
    onSuccess: () => invalidateTenant(qc, tenantId),
  });
}

// ---------------------------------------------------------------- agent picker (ISS-642)

//: Tope de páginas por búsqueda (10 x 100 = 1.000 elementos) — evita bucles
//: ante backends inconsistentes; la búsqueda es server-side sobre el conjunto
//: completo, no solo sobre la primera página.
const PICKER_PAGE_CAP = 10;

/** Todas las hives del tenant (paginado interno; el selector no limita a 20). */
export function usePickerHives() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [...keys.all(tenantId), 'picker-hives'],
    enabled: !!tenantId,
    staleTime: 60 * 1000,
    queryFn: async () => {
      const acc: Hive[] = [];
      for (let page = 1; page <= PICKER_PAGE_CAP; page += 1) {
        const { data } = await apiClient.get<{
          data: Hive[];
          total?: number;
        }>(`/api/v1/hives?page=${page}&page_size=100`);
        acc.push(...(data.data ?? []));
        if (!data.total || acc.length >= data.total) break;
      }
      return acc;
    },
  });
}

/**
 * Búsqueda server-side de agentes sobre el conjunto completo (ISS-642).
 *
 * La carga inicial del selector puede ser la primera página, pero al buscar el
 * término se consulta la API (que busca en todo el tenant) y se paginan los
 * resultados hasta agotarlos (tope de seguridad ``PICKER_PAGE_CAP``).
 */
export function useAgentSearch(term: string) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  const normalized = term.trim();

  return useQuery({
    queryKey: [...keys.all(tenantId), 'agent-search', normalized],
    enabled: !!tenantId && normalized.length > 0,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const acc: Agent[] = [];
      for (let page = 1; page <= PICKER_PAGE_CAP; page += 1) {
        const qs = new URLSearchParams({
          search: normalized,
          page: String(page),
          page_size: '100',
        });
        const { data } = await apiClient.get<PaginatedResponse<Agent>>(
          `/api/v1/tenants/${tenantId}/agents?${qs.toString()}`,
        );
        acc.push(...(data.data ?? []));
        const total = data.meta?.total ?? acc.length;
        if (acc.length >= total) break;
      }
      return acc;
    },
  });
}

// ---------------------------------------------------------------- consents (ISS-644)

export function useFrontierConsents() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [...keys.all(tenantId), 'consents'],
    queryFn: async () => {
      const { data } = await apiClient.get<FrontierConsent[]>(
        `${basePath(tenantId!)}/consents`,
      );
      return data;
    },
    enabled: !!tenantId,
    staleTime: 15 * 1000,
  });
}

export type ConsentAction = 'accept' | 'revoke';

export function useConsentActions() {
  const qc = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async (input: { routineId: string; action: ConsentAction }) => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.post(
        `${basePath(tenantId)}/routines/${input.routineId}/delegations/${input.action}`,
        {},
      );
      return data;
    },
    onSuccess: () => invalidateTenant(qc, tenantId),
  });
}

// ---------------------------------------------------------------- emergency (B)

export function useEmergencyState() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: [...keys.all(tenantId), 'emergency'],
    enabled: !!tenantId,
    staleTime: 15 * 1000,
    queryFn: async () => {
      const { data } = await apiClient.get<EmergencyState>(`${basePath(tenantId!)}/emergency`);
      return data;
    },
  });
}

export function useSetEmergencySwitch() {
  const qc = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  return useMutation({
    mutationFn: async (input: { scope: 'tenant' | 'dr'; enabled: boolean }) => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.post<EmergencyState>(`${basePath(tenantId)}/emergency/switch`, input);
      return data;
    },
    onSuccess: () => invalidateTenant(qc, tenantId),
  });
}

export function useBulkStopRuns() {
  const qc = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  return useMutation({
    mutationFn: async (runIds: string[]) => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.post<BulkStopResult>(
        `${basePath(tenantId)}/runs/bulk-stop`,
        { run_ids: runIds },
      );
      return data;
    },
    onSuccess: () => invalidateTenant(qc, tenantId),
  });
}

export function useAcknowledgeEmergency() {
  const qc = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  return useMutation({
    mutationFn: async (input: { routineId: string; reason: string }) => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.post(`${basePath(tenantId)}/emergency/acknowledge`, {
        routine_id: input.routineId,
        reason: input.reason,
      });
      return data;
    },
    onSuccess: () => invalidateTenant(qc, tenantId),
  });
}

// ---------------------------------------------------------------- retries (C)

export function useRetryRun() {
  const qc = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  return useMutation({
    mutationFn: async (runId: string) => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.post<{ run_id: string; status: string }>(
        `${basePath(tenantId)}/runs/${runId}/retry`,
        {},
      );
      return data;
    },
    onSuccess: () => invalidateTenant(qc, tenantId),
  });
}

// ---------------------------------------------------------------- inbox (D)

export function useInterventions(params: { status?: string } = {}) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: [...keys.all(tenantId), 'interventions', params],
    enabled: !!tenantId,
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
    queryFn: async () => {
      const { data } = await apiClient.get<FrontierInterventionItem[]>(
        `${basePath(tenantId!)}/interventions`,
        { params },
      );
      return data;
    },
  });
}

export function useRespondIntervention() {
  const qc = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  return useMutation({
    mutationFn: async (input: {
      interventionId: string;
      action: 'approve' | 'reject' | 'answer';
      text?: string;
    }) => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.post(
        `${basePath(tenantId)}/interventions/${input.interventionId}/respond`,
        { action: input.action, text: input.text ?? null },
      );
      return data;
    },
    onSuccess: () => invalidateTenant(qc, tenantId),
  });
}

// ---------------------------------------------------------------- platform (E)

export function usePlatformSummary(enabled: boolean) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: [...keys.all(tenantId), 'platform-summary'],
    enabled: enabled && !!tenantId,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const { data } = await apiClient.get<FrontierPlatformSummary>(
        `${basePath(tenantId!)}/platform/summary`,
      );
      return data;
    },
  });
}

// ---------------------------------------------------------------- trigger secrets (Sprint 309)

export interface TriggerSecretReveal {
  triggerId: string;
  pathToken?: string | null;
  signingSecret?: string | null;
}

export function useRotateTrigger() {
  const qc = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async (input: { routineId: string; triggerId: string }) => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.post<{
        id: string;
        path_token?: string | null;
        signing_secret?: string | null;
      }>(`${basePath(tenantId)}/routines/${input.routineId}/triggers/${input.triggerId}/rotate`, {});
      return data;
    },
    onSuccess: () => invalidateTenant(qc, tenantId),
  });
}
